// import { Ai } from 'epc-identifier/ai';
import { Epc } from 'types/epc';
// import { getI18n } from 'services/i18n-service';
import { ScannerBuffer } from 'types/scanner-buffer';
// import { translations } from 'types/translations';

const BARCODE_DELIMITER_CHAR = '$';
const MANUAL_SEPARATOR_CHAR = '^';
const PIPE_SEPARATOR_CHAR = '|';
const SPACE_SEPARATOR_CHAR = ' ';
const BARCODE_DELIMITER_KEY_LAYOUT_CODE = 'Digit4';
const SEPARATOR_KEY_LAYOUT_CODE = 'BracketRight';
const SEPARATOR_KEY_LAYOUT_CODE2 = 'BracketLeft';
const SEPARATOR_KEY_LAYOUT_CODE3 = 'Space';

export const MAX_SCAN_OUTPUT_DELAY = 800;

const separatorCodes = [
  SEPARATOR_KEY_LAYOUT_CODE,
  SEPARATOR_KEY_LAYOUT_CODE2,
  SEPARATOR_KEY_LAYOUT_CODE3
];

const separatorKeys = [
  MANUAL_SEPARATOR_CHAR,
  SPACE_SEPARATOR_CHAR,
  PIPE_SEPARATOR_CHAR,
  String.fromCharCode(29)
];

// cannot use boolean properties on event because some characters we want to show rely on them
const modifierKeys = [
  'Shift',
  'Ctrl',
  'Control',
  'Alt'
];

let buffer: ScannerBuffer = {
  altData: '',
  data: '',
  consecutiveData: '',
  consecutiveKeyCount: 0,
  isAltCode: false,
  isCapsLockOn: false,
  isDebugging: false,
  isRegistering: false,
  isStartRegisteringTimeoutSet: false
};

// const validateAiFieldValues = async (ai: Ai): Promise<string> => {
//   const productCode = ai.find(Ai.gtin);
//   const serial = ai.find(Ai.serial);
//   const batchLot = ai.find(Ai.batchLot);
//   const expirationDate = ai.find(Ai.expirationDate);
//   let statusMessage = '';
//   const i18n = getI18n();

//   if (!productCode || !productCode.value) {
//     statusMessage = i18n.t(translations.ProductCodeRequired);
//   } else if (!serial || !serial.value) {
//     statusMessage = i18n.t(translations.SerialNumberRequired);
//   } else if (!batchLot || !batchLot.value) {
//     statusMessage = i18n.t(translations.BatchIdentifierRequired);
//   } else if (!expirationDate || !expirationDate.value) {
//     statusMessage = i18n.t(translations.ExpiryDateRequired);
//   }

//   if (statusMessage) {
//     statusMessage = i18n.t(translations.UnableToParseEpc) + ' ' + statusMessage;
//     throw new Error(statusMessage);
//   }
//   return statusMessage;
// };

export const parseCode = async (epc: string): Promise<Epc> => {
  // parse code with epc parser
  // const aiGroup = Ai.parseAiGroup(epc);
  // await validateAiFieldValues(aiGroup);

  // get code parts
  // const productCode = aiGroup.find(Ai.gtin);
  // const serialNumber = aiGroup.find(Ai.serial);
  // const lotNumber = aiGroup.find(Ai.batchLot);
  // const expiryDate = aiGroup.find(Ai.expirationDate);

  const item: Epc = {
    rawCode: epc,
    humanReadable: '', // aiGroup.toHumanReadable(),
    productCode: '', // productCode ? productCode.value : '',
    serialNumber: '', // serialNumber ? serialNumber.value : '',
    lotNumber: '', // lotNumber ? lotNumber.value : '',
    expiryDate: '', // expiryDate ? expiryDate.value : '',
    status: ''
  };

  return item;
};

const handleDebugging = (mesage: string) => {
  if (buffer.isDebugging) {
    console.log(mesage);
  }
};

const isDelimitedBy = (char: string, data: string) => {
  return data[0] === char && data[data.length - 1] === char;
};

const writeKeyEventBackToInput = (element: EventTarget | null, value: string) => {
  let input = element as HTMLInputElement | HTMLTextAreaElement;
  const start = input.selectionStart;
  const end = input.selectionEnd;

  if (start !== null && end !== null) {
    const newPosition = start + 1;
    let currentValue: string = input.value;
    if (start !== end) {
      currentValue = currentValue.substr(0, start) + currentValue.substr(end);
    }
    currentValue = [currentValue.slice(0, start), value, currentValue.slice(start)].join('');
    input.value = currentValue;
    input.selectionStart = newPosition;
    input.selectionEnd = newPosition;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
};

const canSendToInput = (data: string): boolean => {
  const MINIMUM_LENGHT_FOR_VALID_INPUT = 1;
  return data.length === MINIMUM_LENGHT_FOR_VALID_INPUT ||
    (data.length > MINIMUM_LENGHT_FOR_VALID_INPUT && !isDelimitedBy(BARCODE_DELIMITER_CHAR, data));
};

const isForAnInput = (event: KeyboardEvent) => {
  return event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement;
};

const sendBackToInput = (data: string) => {
  if (canSendToInput(data) && buffer.lastEvent && isForAnInput(buffer.lastEvent)) {
    writeKeyEventBackToInput(buffer.lastEvent.target, buffer.lastEvent.key);
  }
};

const isBarCode = (data: string): boolean => {
  const MINIMUM_LENGHT_FOR_BARCODE = 3;
  return data.length >= MINIMUM_LENGHT_FOR_BARCODE && isDelimitedBy(BARCODE_DELIMITER_CHAR, data);
};

const isDelimiterKeyEvent = (event: KeyboardEvent) => {
  const normalDigit = /^[0-9]$/i;
  // It's not every scanners that populate the event.code, we also need to check for the key
  let isAccepted = (event.key === BARCODE_DELIMITER_CHAR ||
    event.code === BARCODE_DELIMITER_KEY_LAYOUT_CODE && event.shiftKey && event.key.match(normalDigit) === null);
  handleDebugging('is this is a delimiter? ' + isAccepted + ' {Key: ' + event.key + '}');
  return isAccepted;
};

const resetAltHandler = () => {
  buffer.altData = '';
  buffer.isAltCode = false;
};

const resetTimers = () => {
  if (buffer.timer) {
    clearTimeout(buffer.timer);
    buffer.timer = undefined;
  }

  if (buffer.trackEventTimer) {
    clearTimeout(buffer.trackEventTimer);
    buffer.trackEventTimer = undefined;
  }
};

const resetBuffer = () => {
  resetTimers();
  resetAltHandler();
  buffer.data = '';
  buffer.consecutiveData = '';
  buffer.consecutiveKeyCount = 0;
  buffer.isRegistering = false;
  buffer.isStartRegisteringTimeoutSet = false;
};

const setFirstDelimiterTimeout = (event: KeyboardEvent) => {
  const KEYEVENT_DELAY = 5;
  if (isForAnInput(event) && !buffer.isStartRegisteringTimeoutSet) {
    buffer.lastEvent = event;

    buffer.isStartRegisteringTimeoutSet = true;
    buffer.timer = setTimeout(
      () => {
        const data = buffer.data;

        // If only a $ char is in buffer, send it back to input/textarea.
        if (data.length && !isBarCode(data) && buffer.lastEvent && buffer.lastEvent.target) {
          sendBackToInput(BARCODE_DELIMITER_CHAR);
          resetBuffer();
        }

      },
      KEYEVENT_DELAY);
  }
};

const isStartingInput = (event: KeyboardEvent) => {
  // It's not every scanners that populate the event.code, we also need to check for the key
  return buffer.isRegistering === false && isDelimiterKeyEvent(event);
};

const addDelimiterToBuffer = () => {
  // Not every keyboard layout uses the $ key string on Delimiter Event
  // So we need to make sure to set the delimiter in the buffer.data
  buffer.data += BARCODE_DELIMITER_CHAR;
  handleDebugging('Added delimiter!');
};

const isEndingScanInput = (event: KeyboardEvent) => {
  // It's not every scanners that populate the event.code, we also need to check for the key
  const ENTER_KEY = 'Enter';
  return buffer.data.length > 1 &&
    (event.key === ENTER_KEY || isDelimiterKeyEvent(event));
};

const isConsecutiveDelimiters = (event: KeyboardEvent) => {
  return buffer.data.length === 1 && isDelimiterKeyEvent(event);
};

const isSeparatorKeyEvent = (event: KeyboardEvent) => {
  const key = event.key;
  const code = event.code;
  // Some language ouput [$ + separatorCode + Ctrl=true] as separator.
  return separatorKeys.indexOf(key) !== -1 ||
    ((key !== BARCODE_DELIMITER_CHAR || key === BARCODE_DELIMITER_CHAR && event.ctrlKey) && separatorCodes.indexOf(code) > -1);
};

const isValidEpcInput = (event: KeyboardEvent) => {
  const barcodeInputRegex = /^[\^a-z0-9|\x1D]$/i;
  const key = event.key;
  let isAccepted = key.match(barcodeInputRegex) !== null;

  if (!isAccepted) {
    isAccepted = isSeparatorKeyEvent(event);
    handleDebugging('is this is a separator? ' + isAccepted);
  }

  return isAccepted;
};

const registerAltKey = (event: KeyboardEvent) => {
  buffer.isAltCode = true;
  buffer.altData += event.key;
};

const swallowEvent = (event: KeyboardEvent) => {
  buffer.lastEvent = event;
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
};

const isDebugKey = (event: KeyboardEvent) => {
  const DEBUG_TOGGLE_KEY_1 = 'F2';
  const DEBUG_TOGGLE_KEY_2 = 'F12';
  return event.ctrlKey && (event.key === DEBUG_TOGGLE_KEY_1 || event.key === DEBUG_TOGGLE_KEY_2);
};

const toggleDebugMode = () => {
  buffer.isDebugging = !buffer.isDebugging;
  console.log(buffer.isDebugging ? 'Keyboard debugging Enabled' : 'Keyboard debugging Disabled');
};

const isAltKeydown = (event: KeyboardEvent) => {
  return event.altKey;
};

const isModifierKey = (event: KeyboardEvent) => {
  return modifierKeys.indexOf(event.key) > -1;
};

const startRegistering = (event: KeyboardEvent) => {
  buffer.isRegistering = true;
  swallowEvent(event);
  addDelimiterToBuffer();
  setFirstDelimiterTimeout(event);
};

const stopRegistering = () => {
  buffer.isRegistering = false;
  resetBuffer();
};

const setRegisterTimeout = (onScanAttemptFailed: (output: string) => void) => {

  resetTimers();
  buffer.timer = setTimeout(
    () => {
      // Scan didn't ended properly. Clear the buffer to be safe.
      if (buffer.isDebugging) {
        console.log('scan timeout: ' + buffer.data);
      }
      onScanAttemptFailed(buffer.data);
      resetBuffer();
    },
    MAX_SCAN_OUTPUT_DELAY);
};

const trackEvent = (event: KeyboardEvent, scanAttemptFailed?: (output: string) => void) => {
  const KEYEVENT_DELAY = 10;
  const NUMBER_OF_KEYS_TO_TRIGGER_ALERT = 10;

  buffer.consecutiveData += event.key;
  buffer.consecutiveKeyCount += 1;

  buffer.lastEvent = event;
  resetTimers();
  buffer.trackEventTimer = setTimeout(
    () => {
      if (buffer.consecutiveKeyCount > NUMBER_OF_KEYS_TO_TRIGGER_ALERT &&
        buffer.lastEvent && !isForAnInput(buffer.lastEvent)) {
        // Scanner missconfigured
        if (buffer.isDebugging) {
          console.log('scan attempt timeout: ' + buffer.consecutiveData);
        }
        if (scanAttemptFailed) {
          scanAttemptFailed(buffer.consecutiveData);
        }
      }
      resetBuffer();
    },
    KEYEVENT_DELAY);
};

const sendScannedEpc = (data: string, onEpcScanned: Function) => {
  stopRegistering();

  let epc = data.substr(1, data.length);
  const isSeparatorMissing = !epc.includes(MANUAL_SEPARATOR_CHAR);

  epc = epc.replace(MANUAL_SEPARATOR_CHAR, String.fromCharCode(29));

  handleDebugging('OnEpcScanned: ' + epc);
  onEpcScanned(epc, buffer.isCapsLockOn, isSeparatorMissing);

};

export const registerEvent = (event: KeyboardEvent, onEpcScanned: Function, onScanAttemptFailed: (output: string) => void) => {
  const ALTCODE_GS_CHAR = '0029';

  swallowEvent(event);

  buffer.isCapsLockOn = event.getModifierState('CapsLock');
  if (buffer.isDebugging && buffer.isCapsLockOn) {
    console.log('CapsLock ON');
  }

  if (isValidEpcInput(event)) {
    if (isAltKeydown(event)) {
      registerAltKey(event);
    } else {
      if (buffer.isAltCode) {
        // converting last alt code suite before processing current event
        if (buffer.altData === ALTCODE_GS_CHAR) {
          buffer.data += MANUAL_SEPARATOR_CHAR;
        }
        resetAltHandler();
      }

      handleDebugging('is a valid:');
      if (isSeparatorKeyEvent(event)) {
        // Not every keyboard layout has a key string on BracketRight Event
        // So we need to make sure to set the separator in the buffer.data
        buffer.data += MANUAL_SEPARATOR_CHAR;
        handleDebugging('Converted to manual separator!');
      } else if (event.key.length === 1 && !isDelimiterKeyEvent(event)) {
        // This is a valid keyboard input for barcode
        buffer.data += event.key;
        handleDebugging('added: ' + event.key);
      }
    }
  } else if (isEndingScanInput(event)) {
    // We know we ended, let's send it now
    sendScannedEpc(buffer.data, onEpcScanned);
  } else if (!isModifierKey(event) && !isDelimiterKeyEvent(event)) {
    // This is a valid keyboard input for barcode
    buffer.data += event.key;
    handleDebugging('added: ' + event.key);
  }

  if (buffer.isRegistering) {
    setRegisterTimeout(onScanAttemptFailed);
  }
};

export const handleKeyEvent = (event: KeyboardEvent, onScanAttemptFailed: (output: string) => void,
  onBarcodeScanned: (barcode: string, isCapsLockOn: boolean, isSeparatorMissing: boolean) => void) => {

  if (buffer.isDebugging) {
    console.log(event);
  }

  if (isStartingInput(event)) {
    startRegistering(event);
  } else if (isConsecutiveDelimiters(event)) {
    stopRegistering();
  } else if (buffer.isRegistering) {
    if (onBarcodeScanned) {
      registerEvent(event, onBarcodeScanned, onScanAttemptFailed);
    }
  } else if (isDebugKey(event)) {
    swallowEvent(event);
    toggleDebugMode();
  } else if (!isModifierKey(event)) {
    trackEvent(event, onScanAttemptFailed);
  }
};


// WEBPACK FOOTER //
// ./src/components/scanner/scanner-helper.ts